/**
 * Menu Health Audit Service — Diagnostic engine that scans a MenuProfile
 * for missing data based on professional standards.
 */

import type { MenuProfile, MenuCategory, MenuItem } from '../types';

// ─── Issue Types ───────────────────────────────────────────────────────────────

export type HealthIssueType = 'missing-image' | 'language-gap' | 'pricing-error' | 'description-gap' | 'ministry-permit' | 'empty-profile' | 'empty-category';

export interface HealthIssue {
  type: HealthIssueType;
  severity: 'error' | 'warning';
  /** Human-readable label */
  label: string;
  /** Category id */
  categoryId: string;
  categoryName: string;
  /** Item id (if item-level issue) */
  itemId?: string;
  itemName?: string;
  /** Field key for teleport deep-linking (e.g. 'image', 'nameAR', 'basePrice') */
  fieldKey?: string;
}

export interface HealthReport {
  /** 0–100 */
  score: number;
  issues: HealthIssue[];
  totalChecks: number;
  passedChecks: number;
  summary: {
    missingImages: number;
    languageGaps: number;
    pricingErrors: number;
    descriptionGaps: number;
    ministryPermits: number;
  };
}

// ─── Audit Engine ──────────────────────────────────────────────────────────────

export function auditMenuProfile(profile: MenuProfile): HealthReport {
  const issues: HealthIssue[] = [];
  let totalChecks = 0;

  // ── Ministry permit check ──
  totalChecks++;
  if (!profile.ministryFileIds || profile.ministryFileIds.length === 0) {
    issues.push({
      type: 'ministry-permit', severity: 'error',
      label: `Profile "${profile.name}" has no ministry permit linked`,
      categoryId: '', categoryName: '',
    });
  }
  totalChecks++;
  if (profile.permitSyncRequired) {
    issues.push({
      type: 'ministry-permit', severity: 'error',
      label: `Profile "${profile.name}" requires ministry permit re-certification`,
      categoryId: '', categoryName: '',
    });
  }

  const visibleCategories = profile.categories.filter(c => !c.hidden);

  // ── Empty profile check ──
  totalChecks++;
  if (visibleCategories.length === 0) {
    issues.push({
      type: 'empty-profile', severity: 'error',
      label: `Profile "${profile.name}" has no categories — add at least one category with items`,
      categoryId: '', categoryName: '',
    });
  }

  for (const cat of visibleCategories) {
    const visibleItems = cat.items.filter(i => !i.hidden);

    // ── Empty category check ──
    totalChecks++;
    if (visibleItems.length === 0) {
      issues.push({
        type: 'empty-category', severity: 'warning',
        label: `Category "${cat.nameEN}" has no items`,
        categoryId: cat.id, categoryName: cat.nameEN,
      });
    }

    // Category language check
    totalChecks++;
    if (!cat.nameAR || !cat.nameAR.trim()) {
      issues.push({
        type: 'language-gap', severity: 'warning',
        label: `Category "${cat.nameEN}" missing secondary language name`,
        categoryId: cat.id, categoryName: cat.nameEN, fieldKey: 'nameAR',
      });
    }

    for (const item of visibleItems) {
      // Missing image
      totalChecks++;
      if (!item.image) {
        issues.push({
          type: 'missing-image', severity: 'warning',
          label: `"${item.nameEN}" has no image`,
          categoryId: cat.id, categoryName: cat.nameEN,
          itemId: item.id, itemName: item.nameEN, fieldKey: 'image',
        });
      }

      // Item language gaps (name)
      totalChecks++;
      if (!item.nameAR || !item.nameAR.trim()) {
        issues.push({
          type: 'language-gap', severity: 'warning',
          label: `"${item.nameEN}" missing secondary language name`,
          categoryId: cat.id, categoryName: cat.nameEN,
          itemId: item.id, itemName: item.nameEN, fieldKey: 'nameAR',
        });
      }

      // Item language gaps (description)
      totalChecks++;
      if (item.descriptionEN && item.descriptionEN.trim() && (!item.descriptionAR || !item.descriptionAR.trim())) {
        issues.push({
          type: 'language-gap', severity: 'warning',
          label: `"${item.nameEN}" missing secondary language description`,
          categoryId: cat.id, categoryName: cat.nameEN,
          itemId: item.id, itemName: item.nameEN, fieldKey: 'descriptionAR',
        });
      }

      // Pricing errors — skip if option groups provide pricing
      totalChecks++;
      if (!item.basePrice || item.basePrice <= 0) {
        const hasPricedOptions = (item.optionGroups ?? []).some(
          g => !g.hidden && g.options.some(o => !o.hidden && o.extraPrice > 0),
        );
        if (!hasPricedOptions) {
          issues.push({
            type: 'pricing-error', severity: 'error',
            label: `"${item.nameEN}" has ${item.basePrice === 0 ? 'zero' : 'missing'} price`,
            categoryId: cat.id, categoryName: cat.nameEN,
            itemId: item.id, itemName: item.nameEN, fieldKey: 'basePrice',
          });
        }
      }

      // Description gaps
      totalChecks++;
      if (!item.descriptionEN || !item.descriptionEN.trim()) {
        issues.push({
          type: 'description-gap', severity: 'warning',
          label: `"${item.nameEN}" has no description`,
          categoryId: cat.id, categoryName: cat.nameEN,
          itemId: item.id, itemName: item.nameEN, fieldKey: 'descriptionEN',
        });
      }
    }
  }

  const passedChecks = totalChecks - issues.length;
  const score = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 100;

  return {
    score,
    issues,
    totalChecks,
    passedChecks,
    summary: {
      missingImages: issues.filter(i => i.type === 'missing-image').length,
      languageGaps: issues.filter(i => i.type === 'language-gap').length,
      pricingErrors: issues.filter(i => i.type === 'pricing-error').length,
      descriptionGaps: issues.filter(i => i.type === 'description-gap').length,
      ministryPermits: issues.filter(i => i.type === 'ministry-permit').length,
    },
  };
}

/** Color and icon config for issue types */
export const ISSUE_CONFIG: Record<HealthIssueType, { color: string; bg: string; border: string; emoji: string; label: string }> = {
  'missing-image':   { color: 'text-red-600',    bg: 'bg-red-50',    border: 'border-red-200',    emoji: '🔴', label: 'Missing Images' },
  'language-gap':    { color: 'text-amber-600',   bg: 'bg-amber-50',  border: 'border-amber-200',  emoji: '🟡', label: 'Language Gaps' },
  'pricing-error':   { color: 'text-red-600',     bg: 'bg-red-50',    border: 'border-red-200',    emoji: '🔴', label: 'Pricing Errors' },
  'description-gap': { color: 'text-blue-600',    bg: 'bg-blue-50',   border: 'border-blue-200',   emoji: '🔵', label: 'Description Gaps' },
  'ministry-permit': { color: 'text-purple-600',  bg: 'bg-purple-50', border: 'border-purple-200', emoji: '🟣', label: 'Ministry Permits' },
  'empty-profile':   { color: 'text-slate-600',   bg: 'bg-slate-50',  border: 'border-slate-200',  emoji: '⚫', label: 'Empty Profiles' },
  'empty-category':  { color: 'text-orange-600',  bg: 'bg-orange-50', border: 'border-orange-200', emoji: '🟠', label: 'Empty Categories' },
};
